import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {
  SaveToResultsStepDataName,
  SendToDocumentStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import getOutputStepDataForDataFrame, {
  getOutputStepDataForChart,
} from 'analysis-tool/data/getOutputStepDataForDataFrame';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible, assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  SAVE_TO_RESULTS_STARTED_EVENT,
  SEND_TO_DOCUMENT_STARTED_EVENT,
} from 'performance/processAnalysesEvents';
import {AddStepWithParentStepReference, ParentStepReference, StepReference} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';

import {getOutputNameFromOutput, isSendToDocumentEnabled} from './OutputsUtil';
import {AnalysisOutput, AnalysisOutputType, ChartOutputConfig} from './Types';

function datasetOutputToStepParam(
  analysisName: string,
  output: AnalysisOutput,
  folderId: string
): AddStepWithParentStepReference<PipelineStepType> {
  assert(output.outputConfig.type === AnalysisOutputType.DATASET, "Output must be of type 'DATASET'");
  return {
    step: {
      stepType: PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
      stepVersion: 0,
      stepConfig: {
        folderId,
        datasetName: getOutputNameFromOutput(analysisName, output.outputConfig),
      },
      stepInputParents: {
        [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
          {
            stepReference: {stepId: output.outputConfig.step.id, type: StepReference.STEP_ID},
            stepDataName: getOutputStepDataForDataFrame(
              output.outputConfig.step,
              output.outputConfig.dataFrame.id
            )!.name,
          },
        ],
      },
      stepInput: {inputDataFrameId: output.outputConfig.dataFrame.id},
    },
  };
}

function resultOutputToStepParam(output: AnalysisOutput): AddStepWithParentStepReference<PipelineStepType> {
  assert(output.outputConfig.type === AnalysisOutputType.RESULT, "Output must be of type 'RESULT'");
  const dataMapping = output.outputConfig.newDataMapping;
  const targetSpecificMappingState = output.outputConfig.targetSpecificMappingState;

  return {
    step: {
      stepType: PipelineStepType.SAVE_TO_RESULTS,
      stepVersion: 2,
      stepConfig: {
        schemaId: dataMapping.resultSchema!.id,
        columnMapping: dataMapping.mappings,
        targetSpecificMapping: targetSpecificMappingState,
      },
      stepInputParents: {
        [SaveToResultsStepDataName.INPUT_DATASET]: [
          {
            stepReference: {stepId: output.outputConfig.step.id, type: StepReference.STEP_ID},
            stepDataName: getOutputStepDataForDataFrame(
              output.outputConfig.step,
              output.outputConfig.dataFrame.id
            )!.name,
          },
        ],
      },
      stepInput: {inputDataFrameId: output.outputConfig.dataFrame.id},
    },
  };
}

function isSendingToDocument(selectedOutputs: Array<AnalysisOutput>): boolean {
  return isSendToDocumentEnabled() && selectedOutputs.length > 0;
}

interface DocumentStepInputParent {
  [SendToDocumentStepDataName.INPUT_CHARTS]: Array<ParentStepReference>;
  [SendToDocumentStepDataName.INPUT_DATASETS]: Array<ParentStepReference>;
  [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: Array<ParentStepReference>;
}

function getSendToDocumentStepInputParents(
  newStepParams: Array<AddStepWithParentStepReference<PipelineStepType>>,
  chartOutputs: Array<AnalysisOutput>
): DocumentStepInputParent {
  const datasetInputs: Array<ParentStepReference> = [];
  const savedToResultsInputs: Array<ParentStepReference> = [];
  const chartInputs: Array<ParentStepReference> = [];
  newStepParams.forEach((stepParam, index) => {
    if (stepParam.step.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
      datasetInputs.push({
        stepReference: {
          index,
          type: StepReference.INDEX,
        },
        stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
      });
    } else {
      savedToResultsInputs.push({
        stepReference: {
          index,
          type: StepReference.INDEX,
        },
        stepDataName: SaveToResultsStepDataName.SAVED_RESULTS,
      });
    }
  });
  chartOutputs.forEach((output) => {
    const outputStepData = getOutputStepDataForChart(
      output.outputConfig.step,
      (output.outputConfig as ChartOutputConfig).chart.id
    );

    const chartIndexInStep =
      outputStepData?.charts.findIndex(
        (c) =>
          !isInaccessible(c) &&
          assertIsAccessible(c).id === (output.outputConfig as ChartOutputConfig).chart.id
      ) ?? 0;

    chartInputs.push({
      stepReference: {
        stepId: output.outputConfig.step.id,
        type: StepReference.STEP_ID,
      },
      stepDataName: outputStepData!.name,
      fromStepDataIndex: chartIndexInStep,
    });
  });

  return {
    [SendToDocumentStepDataName.INPUT_CHARTS]: chartInputs,
    [SendToDocumentStepDataName.INPUT_DATASETS]: datasetInputs,
    [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: savedToResultsInputs,
  };
}

function getSendToDocumentStepParams(
  sectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0>['sectionLayout'],
  documentId: string,
  stepInputParents: DocumentStepInputParent
): AddStepWithParentStepReference<PipelineStepType> {
  return {
    step: {
      stepType: PipelineStepType.SEND_TO_DOCUMENT,
      stepVersion: 0,
      stepConfig: {
        documentId,
        sectionLayout,
        sectionIndex: null,
      },
      stepInputParents,
    },
  };
}

export interface CreateOutputParams {
  analysis: AnalysisGraphQL;
  documentSectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0>['sectionLayout'];
  selectedDocumentId: string | null;
  selectedFolderId: string | null;
  selectedOutputs: Array<AnalysisOutput>;
}

export default async function createOutputs({
  analysis,
  documentSectionLayout,
  selectedDocumentId,
  selectedFolderId,
  selectedOutputs,
}: CreateOutputParams): Promise<GetAnalysisPipelineStep> {
  const datasetOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.DATASET
  );
  const resultOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.RESULT
  );
  const chartOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.CHART
  );

  if (isSendingToDocument(selectedOutputs)) {
    logProfilerTimingEvent(SEND_TO_DOCUMENT_STARTED_EVENT);
  }
  if (resultOutputs.length > 0) {
    logProfilerTimingEvent(SAVE_TO_RESULTS_STARTED_EVENT);
  }

  assert(
    selectedFolderId != null || datasetOutputs.length === 0,
    'selectedFolderId must be defined if there are datasets',
    {
      selectedFolderId,
    }
  );
  const newStepParams: Array<AddStepWithParentStepReference<PipelineStepType>> = [
    ...datasetOutputs.map((output) => datasetOutputToStepParam(analysis.name, output, selectedFolderId!)),
    ...resultOutputs.map((output) => resultOutputToStepParam(output)),
  ];

  const sendingToDocument = isSendingToDocument(selectedOutputs);
  if (sendingToDocument) {
    assert(selectedDocumentId != null, 'selectedDocumentId must be defined', {selectedDocumentId});
    const stepInputParents = getSendToDocumentStepInputParents(newStepParams, chartOutputs);

    const sendToDocumentStepParams = getSendToDocumentStepParams(
      documentSectionLayout,
      selectedDocumentId,
      stepInputParents
    );
    newStepParams.push(sendToDocumentStepParams);
  }
  const pipelineIndexToShow = sendingToDocument ? newStepParams.length - 1 : 0;
  return (await AnalysisActions.addAndRunAnalysisStepsV2(analysis.id, newStepParams))[pipelineIndexToShow];
}
